import { useEffect, useState } from "react";
import { MessageCircle, Phone, Instagram, Facebook, Youtube, Globe } from "lucide-react";
import { api } from "../../lib/api";
import { useLang } from "../../lib/i18n";

const SnapIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M12 2c-3 0-5.2 2.3-5.2 5.4v1.9c-.5-.2-1-.3-1.5-.2-.9.2-1.4 1.1-1 1.9.3.5.7.8 1.3 1.1.4.2.6.4.7.6.1.6-.6 1.2-1.7 1.7-.4.2-.9.4-1.2.5-.6.2-.9.8-.7 1.4.1.4.5.7.9.8.4.1.8.2 1.2.2.2.3.4.7.7 1 .2.4.7.6 1.1.6.6-.1 1.2-.1 1.7-.1.9 0 1.7.2 2.4.6.6.3 1.2.5 1.9.5s1.4-.2 1.9-.5c.7-.4 1.5-.6 2.4-.6.5 0 1.1.1 1.7.1.4 0 .9-.2 1.1-.6.2-.3.5-.7.7-1 .4-.1.8-.1 1.2-.2.4-.1.8-.4.9-.8.2-.6-.1-1.2-.7-1.4-.4-.2-.9-.3-1.2-.5-1.1-.4-1.8-1-1.7-1.7 0-.2.3-.4.7-.6.6-.3 1-.7 1.3-1.1.4-.8-.1-1.7-1-1.9-.5-.1-1 0-1.5.2V7.4C17.2 4.3 15 2 12 2z" />
    </svg>
);

const TikTokIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
);

const XIcon = ({ className }) => (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
        <path d="M18.9 2H22l-6.8 7.8L23.2 22h-6.3l-4.9-6.4L6.3 22H3.2l7.3-8.3L2.8 2h6.4l4.4 5.9L18.9 2zm-1.1 18h1.7L7.1 3.9H5.3L17.8 20z" />
    </svg>
);

const PLATFORM_ICONS = {
    whatsapp: MessageCircle,
    phone: Phone,
    instagram: Instagram,
    snapchat: SnapIcon,
    tiktok: TikTokIcon,
    twitter: XIcon,
    facebook: Facebook,
    youtube: Youtube,
    other: Globe,
};

let cache = null;
let inflight = null;

export const useSocials = () => {
    const [socials, setSocials] = useState(cache || []);
    useEffect(() => {
        if (cache) return;
        if (!inflight) {
            inflight = api.get("/socials").then((r) => { cache = r.data; return r.data; }).catch(() => []);
        }
        inflight.then(setSocials);
    }, []);
    return socials;
};

export const SocialIcons = ({ compact = false }) => {
    const socials = useSocials();
    const { t } = useLang();

    return (
        <div data-testid="social-icons-row">
            {!compact && (
                <p className="text-xs text-slate-500 mb-3">{t("ftr_follow")}</p>
            )}
            <div className="flex items-center gap-2.5 flex-wrap">
                {socials.map((s) => {
                    const Icon = PLATFORM_ICONS[s.platform] || Globe;
                    return (
                        <a
                            key={s.id}
                            href={s.url}
                            target={s.url.startsWith("http") ? "_blank" : undefined}
                            rel="noreferrer"
                            aria-label={s.handle || s.platform}
                            title={s.handle || s.platform}
                            data-testid={`social-${s.platform}`}
                            className="w-10 h-10 rounded-full border border-white/15 bg-white/[0.04] flex items-center justify-center text-slate-300 hover:text-amber-400 hover:border-amber-400/50 hover:-translate-y-0.5 transition-[color,border-color,transform] duration-300"
                        >
                            <Icon className="w-5 h-5" />
                        </a>
                    );
                })}
            </div>
        </div>
    );
};
